import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { InvisibleCodeResponse } from '../types';
import { getInvisibleCode } from '../api/visitor';
import styled from '@emotion/styled';
import { GooglePlayButton, AppStoreButton } from 'react-mobile-app-button';

const InvisibleCodeView = () => {
  const { confirmCode } = useParams<{ confirmCode: string }>();

  const [data, setData] = useState<InvisibleCodeResponse['data'] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInvisibbleCode = async () => {
      if (!confirmCode) {
        setError('참가고유번호가 없습니다.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getInvisibleCode(confirmCode);

        if (response.success) {
          setData(response.data);
        }
      } catch (error: any) {
        if (error.response?.data?.message) {
          setError(error.response.data.message);
        } else if (error.message) {
          setError(error.message);
        } else {
          setError('인비저블 코드 조회 중 오류가 발생했습니다.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchInvisibbleCode();
  }, [confirmCode]);

  if (loading) {
    return <LoadingContainer>로딩 중...</LoadingContainer>;
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }

  if (!data) {
    return <Container>데이터가 없습니다.</Container>;
  }

  return (
    <Container>
      <ContentWrapper>
        {/* 상단: Invisible 이미지 영역 */}
        <ImageSection>
          <InvisibleImage src={data.invisible_image_url} alt="STARTUP INSIDE 2025" />
        </ImageSection>

        {/* 중간: 방문자 정보 영역 */}
        <InfoCard>
          <InfoRow>
            <InfoLabel>참가고유번호</InfoLabel>
            <InfoValue highlight>{data.confirm_code}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>회사명</InfoLabel>
            <InfoValue>{data.visitor_company_name}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>성함</InfoLabel>
            <InfoValue>{data.visitor_name}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>직급</InfoLabel>
            <InfoValue>{data.visitor_position || '사원'}</InfoValue>
          </InfoRow>
        </InfoCard>

        {/* 하단: 안내 영역 */}
        <GuideSection>
          <GuideText>
            본 Invisible Code를 안내원을 통해 스캔 후<br />
            출입증을 수령하여 입장해주세요
          </GuideText>
          <GuideTextEn>
            Please scan this Invisible Code,
            <br />
            print out the pass and enter
          </GuideTextEn>
        </GuideSection>
        {/* 앱 다운로드 버튼 영역 추가 */}
        <AppDownloadSection>
          <DownloadTitle>UREKA 앱 다운로드</DownloadTitle>
          <ButtonContainer>
            <GooglePlayButton
              url={'https://play.google.com/store/apps/details?id=com.icon.inocamx2&hl=ko'}
              theme={'dark'}
              className={'custom-style'}
            />
            <AppStoreButton
              url={'https://apps.apple.com/kr/app/ureka-urcode-scanner/id6738121169'}
              theme={'dark'}
              className={'custom-style'}
            />
          </ButtonContainer>
        </AppDownloadSection>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 374px;
  background-image: url('/samsung-bg.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 12px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ImageSection = styled.div`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
`;

const InvisibleImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.95); // 약간 투명한 흰색
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const InfoValue = styled.span<{ highlight?: boolean }>`
  font-size: ${(props) => (props.highlight ? '18px' : '16px')};
  color: ${(props) => (props.highlight ? '#003CDC' : '#333')};
  font-weight: ${(props) => (props.highlight ? '700' : '600')};
`;

const GuideSection = styled.div`
  background: rgba(248, 249, 250, 0.95);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
`;

const GuideText = styled.p`
  font-size: 15px;
  color: #333;
  line-height: 1.6;
  margin-bottom: 12px;
  font-weight: 500;
`;

const GuideTextEn = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  text-align: center;
  font-size: 16px;
  color: #666;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  padding: 20px;
  background-color: white;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  text-align: center;
  max-width: 374px;
`;
const AppDownloadSection = styled.div`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
`;

const DownloadTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;

  .custom-style {
    transform: scale(0.9);
  }

  @media (min-width: 480px) {
    flex-direction: row;
    justify-content: center;
  }
`;

export default InvisibleCodeView;
